import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut, adminPostForm } from "lib/adminClient";
import { mediaUrl } from "lib/media";
import SlideOptions from "components/admin/SlideOptions";
import SlideImages from "components/admin/SlideImages";

/**
 * Editor for the per-institution public pages (/institutions/:slug).
 * One entry here produces one page — adding an institution needs no code change.
 */

const COLORS = [
  ["#005C40", "Green"],
  ["#8B1A4A", "Rose"],
  ["#9B7200", "Gold"],
  ["#B8002B", "Red"],
  ["#003d2b", "Dark Green"],
  ["#2d6ab0", "Blue"],
];

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";
const labelCls = "text-[12px] font-semibold text-muted block mb-1.5";
const addBtn =
  "bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90";
const subBtn =
  "bg-green-light text-green-dark border-none rounded-md px-2.5 py-1 font-sans font-semibold text-[11px] cursor-pointer hover:opacity-80";
const delBtn =
  "bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44] shrink-0";

const blankInstitution = () => ({
  slug: "",
  short: "",
  name: "",
  kicker: "",
  color: "#005C40",
  noticeTag: "",
  heroBg: "linear-gradient(140deg,#003d2b 0%,#005C40 55%,#007a55 100%)",
  slides: [{ headline: "", sub: "", image: null }],
  stats: [],
  overviewTitle: "",
  overviewDesc: "",
  features: [],
  admission: {
    label: "Admission",
    title: "",
    desc: "",
    primaryLabel: "Apply Now",
    primaryHref: "/admissions",
    secondaryLabel: "Notices",
    secondaryHref: "/notices",
  },
  curriculumLabel: "Curriculum",
  curriculumTitle: "Stages & Subjects",
  groups: [],
  curriculumCtaLabel: "See full academics & facilities",
  curriculumCtaHref: "/academics",
});

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <span className="text-[11.5px] text-muted mt-1 block">{hint}</span>}
    </div>
  );
}

/** Generic list-of-objects editor used for slides, stats and features. */
function RowList({ title, rows, cols, onChange, blank, imageKey, renderExtra }) {
  const [uploading, setUploading] = useState(null);

  // Uploads go to the shared media endpoint and store the returned path, the
  // same way the homepage hero slides do.
  const uploadImage = async (i, file) => {
    if (!file) return;
    setUploading(i);
    try {
      const form = new FormData();
      form.append("image", file);
      const { url } = await adminPostForm("/admin/media", form);
      onChange(rows.map((r, idx) => (idx === i ? { ...r, [imageKey]: url } : r)));
    } catch {
      /* surfaced by the page-level error banner on next save */
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="border-l-2 border-line pl-3.5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] font-semibold text-muted uppercase tracking-[0.08em]">
          {title}
        </span>
        <button onClick={() => onChange([...(rows || []), blank()])} className={subBtn}>
          + Add
        </button>
      </div>
      {(rows || []).length === 0 && <div className="text-muted text-[13px] mb-2">None yet.</div>}
      <div className="flex flex-col gap-2">
        {(rows || []).map((row, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
              {cols.map(([key, ph, wide]) => (
                <input
                  key={key}
                  value={row[key] || ""}
                  onChange={(e) =>
                    onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: e.target.value } : r)))
                  }
                  placeholder={ph}
                  className={`${inputCls} ${wide ? "md:col-span-2" : ""}`}
                />
              ))}

              {imageKey && (
                <div className="md:col-span-2 flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-[12.5px] font-semibold text-green">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadImage(i, e.target.files?.[0])}
                    />
                    <span className="px-3 py-1.5 border-[1.5px] border-line rounded-lg hover:border-green">
                      {uploading === i ? "Uploading…" : row[imageKey] ? "Replace photo" : "＋ Background photo"}
                    </span>
                  </label>

                  {row[imageKey] && (
                    <>
                      <span
                        className="w-16 h-10 rounded-md bg-cover bg-center border border-line shrink-0"
                        style={{ backgroundImage: `url(${mediaUrl(row[imageKey])})` }}
                      />
                      <button
                        onClick={() =>
                          onChange(rows.map((r, idx) => (idx === i ? { ...r, [imageKey]: null } : r)))
                        }
                        className="text-[12px] text-[#c44] bg-transparent border-none cursor-pointer p-0"
                      >
                        Remove photo
                      </button>
                    </>
                  )}
                </div>
              )}

              {renderExtra && (
                <div className="md:col-span-2">
                  {renderExtra(row, i, (patch) =>
                    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
              title="Remove"
              className={delBtn}
            >
              🗑
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ---------------------------------------------------------------- */

/** Titled panel so the editor reads as a few clear groups, not one long form. */
function Section({ title, hint, children }) {
  return (
    <section className="border border-line rounded-xl overflow-hidden bg-white">
      <div className="bg-[#f6f8f7] px-4 py-2.5 border-b border-line">
        <div className="text-[12.5px] font-bold text-ink">{title}</div>
        {hint && <div className="text-[11.5px] text-muted mt-0.5">{hint}</div>}
      </div>
      <div className="p-4 flex flex-col gap-3.5">{children}</div>
    </section>
  );
}

/* Hero background is stored as a CSS gradient string, but nobody editing the
   site should have to type CSS — this offers presets plus two colour pickers
   and only exposes the raw value behind an "Advanced" toggle. */
const GRADIENT_PRESETS = [
  ["Green", "#003d2b", "#007a55"],
  ["Maroon", "#3a0015", "#8B1A4A"],
  ["Gold", "#1a1200", "#9B7200"],
  ["Red", "#3a0010", "#B8002B"],
  ["Blue", "#0a2340", "#2d6ab0"],
  ["Charcoal", "#14211c", "#3d5a4e"],
];

const buildGradient = (from, to) => `linear-gradient(140deg,${from} 0%,${to} 100%)`;

/** Pull the first and last colour out of whatever CSS is stored. */
function readStops(css) {
  const hexes = String(css || "").match(/#[0-9a-fA-F]{6}/g) || [];
  return [hexes[0] || "#003d2b", hexes[hexes.length - 1] || "#005C40"];
}

function HeroBackground({ value, onChange }) {
  const [advanced, setAdvanced] = useState(false);
  const [from, to] = readStops(value);
  const preview = value || buildGradient(from, to);

  return (
    <div className="flex flex-col gap-3">
      <div
        className="h-16 rounded-lg border border-line"
        style={{ background: preview }}
        aria-label="Background preview"
      />

      <div>
        <div className="text-[11.5px] font-semibold text-muted mb-1.5">Ready-made</div>
        <div className="flex gap-2 flex-wrap">
          {GRADIENT_PRESETS.map(([name, a, b]) => {
            const css = buildGradient(a, b);
            const on = preview === css;
            return (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => onChange(css)}
                className="w-14 h-9 rounded-lg cursor-pointer transition-transform hover:scale-105"
                style={{
                  background: css,
                  border: on ? "2.5px solid #005C40" : "1px solid #e2ddd6",
                }}
              />
            );
          })}
        </div>
      </div>

      <div className="flex items-end gap-4 flex-wrap">
        <label className="flex flex-col gap-1">
          <span className="text-[11.5px] font-semibold text-muted">Start colour</span>
          <input
            type="color"
            value={from}
            onChange={(e) => onChange(buildGradient(e.target.value, to))}
            className="w-16 h-9 border border-line rounded-lg cursor-pointer bg-white"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11.5px] font-semibold text-muted">End colour</span>
          <input
            type="color"
            value={to}
            onChange={(e) => onChange(buildGradient(from, e.target.value))}
            className="w-16 h-9 border border-line rounded-lg cursor-pointer bg-white"
          />
        </label>

        <button
          type="button"
          onClick={() => setAdvanced((a) => !a)}
          className="text-[11.5px] text-muted bg-transparent border-none cursor-pointer underline p-0 ml-auto"
        >
          {advanced ? "Hide CSS" : "Advanced (CSS)"}
        </button>
      </div>

      {advanced && (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="linear-gradient(140deg,#003d2b 0%,#007a55 100%)"
          className={inputCls}
        />
      )}
    </div>
  );
}

function InstitutionEditor({ inst, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const set = (k, v) => onChange({ ...inst, [k]: v });
  const setAdm = (k, v) => onChange({ ...inst, admission: { ...(inst.admission || {}), [k]: v } });

  return (
    <div className="border-[1.5px] rounded-xl" style={{ borderColor: inst.color }}>
      <div className="flex items-center gap-3 p-3.5">
        <span
          className="w-10 h-9 rounded-lg flex items-center justify-center font-serif font-bold text-[12px] shrink-0"
          style={{ background: `${inst.color}18`, color: inst.color }}
        >
          {inst.short || "—"}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-ink truncate">
            {inst.name || "Untitled institution"}
          </div>
          <div className="text-[11.5px] text-muted">/institutions/{inst.slug || "…"}</div>
        </div>
        <button onClick={() => setOpen((o) => !o)} className={subBtn}>
          {open ? "Collapse" : "Edit"}
        </button>
        <button onClick={onRemove} title="Remove institution" className={delBtn}>
          🗑
        </button>
      </div>

      {open && (
        <div className="border-t border-line bg-[#fafbfa] p-4 flex flex-col gap-4">
          <Section title="Basics" hint="Identity and where this institution's page lives.">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="URL slug" hint="Page lives at /institutions/<slug>">
              <input value={inst.slug} onChange={(e) => set("slug", e.target.value.trim())} placeholder="kcns" className={inputCls} />
            </Field>
            <Field label="Badge / short">
              <input value={inst.short} onChange={(e) => set("short", e.target.value)} placeholder="KCNS" className={inputCls} />
            </Field>
            <Field label="Colour">
              <select value={inst.color} onChange={(e) => set("color", e.target.value)} className={`${inputCls} bg-white`}>
                {COLORS.map(([hex, n]) => (
                  <option key={hex} value={hex}>{n}</option>
                ))}
              </select>
            </Field>
            <Field label="Notice tag" hint="Matches the tag on notices.">
              <input value={inst.noticeTag} onChange={(e) => set("noticeTag", e.target.value)} placeholder="KCNS" className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Full name">
              <input value={inst.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Kicker">
              <input value={inst.kicker} onChange={(e) => set("kicker", e.target.value)} placeholder="Primary Wing" className={inputCls} />
            </Field>
          </div>
          </Section>

          <Section title="Hero" hint="The banner at the top of the page. Add photos to turn it into a slider.">
            <Field label="Background colour">
              <HeroBackground value={inst.heroBg} onChange={(v) => set("heroBg", v)} />
            </Field>

            <RowList
              title="Hero slides"
              rows={inst.slides}
              blank={() => ({ headline: "", sub: "", image: null, mobileImage: null })}
              cols={[["headline", "Headline"], ["sub", "Sub-line", true]]}
              onChange={(v) => set("slides", v)}
              renderExtra={(row, i, patch) => (
                <>
                {/* Desktop + mobile photo uploads (shared picker) */}
                <div className="mb-2">
                  <SlideImages slide={row} variant="institution" onChange={patch} />
                </div>
                <SlideOptions
                  slide={row}
                  index={i}
                  defaultDuration={6}
                  onChange={patch}
                  onApplyAnimationToAll={(animation) =>
                    set(
                      "slides",
                      (inst.slides || []).map((r) => ({ ...r, animation: animation === "fade" ? undefined : animation }))
                    )
                  }
                />
                </>
              )}
            />
          </Section>

          <Section title="Stats strip" hint="The green band of numbers under the hero.">
            <RowList
              title="Stats"
              rows={inst.stats}
              blank={() => ({ value: "", label: "" })}
              cols={[["value", "112"], ["label", "Young learners"]]}
              onChange={(v) => set("stats", v)}
            />
          </Section>

          <Section title="Overview" hint="Intro paragraph and the small highlight cards beside it.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Overview title">
              <input value={inst.overviewTitle} onChange={(e) => set("overviewTitle", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Overview description">
              <textarea value={inst.overviewDesc} onChange={(e) => set("overviewDesc", e.target.value)} rows={2} className={`${inputCls} resize-y`} />
            </Field>
          </div>

          <RowList
            title="Feature cards"
            rows={inst.features}
            blank={() => ({ title: "", desc: "" })}
            cols={[["title", "Play-based early years"], ["desc", "Short description", true]]}
            onChange={(v) => set("features", v)}
          />
          </Section>

          <Section title="Admission card" hint="The coloured box in the sidebar, with up to two buttons.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <Field label="Label"><input value={inst.admission?.label || ""} onChange={(e) => setAdm("label", e.target.value)} className={inputCls} /></Field>
              <Field label="Title"><input value={inst.admission?.title || ""} onChange={(e) => setAdm("title", e.target.value)} className={inputCls} /></Field>
            </div>
            <Field label="Description">
              <textarea value={inst.admission?.desc || ""} onChange={(e) => setAdm("desc", e.target.value)} rows={2} className={`${inputCls} resize-y mb-3`} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Button 1 text"><input value={inst.admission?.primaryLabel || ""} onChange={(e) => setAdm("primaryLabel", e.target.value)} className={inputCls} /></Field>
              <Field label="Button 1 link"><input value={inst.admission?.primaryHref || ""} onChange={(e) => setAdm("primaryHref", e.target.value)} className={inputCls} /></Field>
              <Field label="Button 2 text"><input value={inst.admission?.secondaryLabel || ""} onChange={(e) => setAdm("secondaryLabel", e.target.value)} className={inputCls} /></Field>
              <Field label="Button 2 link"><input value={inst.admission?.secondaryHref || ""} onChange={(e) => setAdm("secondaryHref", e.target.value)} className={inputCls} /></Field>
            </div>
          </Section>

          <Section title="Curriculum" hint="Heading plus the programme cards shown below the overview.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Curriculum label"><input value={inst.curriculumLabel} onChange={(e) => set("curriculumLabel", e.target.value)} className={inputCls} /></Field>
            <Field label="Curriculum heading"><input value={inst.curriculumTitle} onChange={(e) => set("curriculumTitle", e.target.value)} className={inputCls} /></Field>
          </div>

          {/* Group cards */}
          <div className="border-l-2 border-line pl-3.5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-semibold text-muted uppercase tracking-[0.08em]">
                Group / programme cards
              </span>
              <button
                onClick={() => set("groups", [...(inst.groups || []), { name: "", icon: "📘", color: inst.color, subs: [], variant: "", desc: "" }])}
                className={subBtn}
              >
                + Add card
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {(inst.groups || []).map((g, gi) => {
                const setG = (k, v) =>
                  set("groups", inst.groups.map((x, idx) => (idx === gi ? { ...x, [k]: v } : x)));
                return (
                  <div key={gi} className="border border-line rounded-lg p-3 grid grid-cols-1 md:grid-cols-[56px_1fr_2fr_auto] gap-2 items-start">
                    <input value={g.icon || ""} onChange={(e) => setG("icon", e.target.value)} placeholder="📘" className={`${inputCls} text-center`} />
                    <input value={g.name || ""} onChange={(e) => setG("name", e.target.value)} placeholder="Card name" className={inputCls} />
                    {g.variant === "plain" ? (
                      <input value={g.desc || ""} onChange={(e) => setG("desc", e.target.value)} placeholder="Description text" className={inputCls} />
                    ) : (
                      <input
                        value={(g.subs || []).join(", ")}
                        onChange={(e) => setG("subs", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                        placeholder="Subjects, comma separated"
                        className={inputCls}
                      />
                    )}
                    <div className="flex gap-1.5">
                      <label title="Plain card — title + description, no subject list" className="flex items-center gap-1 text-[11px] text-muted cursor-pointer border border-line rounded-md px-2">
                        <input type="checkbox" checked={g.variant === "plain"} onChange={(e) => setG("variant", e.target.checked ? "plain" : "")} className="cursor-pointer" />
                        Plain
                      </label>
                      <button onClick={() => set("groups", inst.groups.filter((_, idx) => idx !== gi))} title="Remove card" className={delBtn}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Curriculum button text"><input value={inst.curriculumCtaLabel} onChange={(e) => set("curriculumCtaLabel", e.target.value)} className={inputCls} /></Field>
            <Field label="Curriculum button link"><input value={inst.curriculumCtaHref} onChange={(e) => set("curriculumCtaHref", e.target.value)} className={inputCls} /></Field>
          </div>
          </Section>
        </div>
      )}
    </div>
  );
}

export default function InstitutionsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/institutions")) || {};
      setItems(d.items || []);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    // A slug is what the URL is built from — an entry without one has no page.
    const cleaned = items.filter((i) => (i.slug || "").trim() && (i.name || "").trim());
    const slugs = cleaned.map((i) => i.slug);
    if (new Set(slugs).size !== slugs.length) {
      setError("Two institutions share the same URL slug. Slugs must be unique.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const saved = await adminPut("/admin/content/institutions", { items: cleaned });
      if (saved) setItems(saved.items || []);
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}
      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title={`Institutions (${items.length})`}
          action={
            <button onClick={() => setItems([...items, blankInstitution()])} className={addBtn}>
              + Add Institution
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          <div className="text-[12.5px] text-muted -mt-1">
            Each entry becomes a public page at <code>/institutions/&lt;slug&gt;</code> and appears in
            the site header's Institutions menu.
          </div>
          {items.length === 0 && <div className="text-muted text-sm">No institutions yet.</div>}
          {items.map((it, i) => (
            <InstitutionEditor
              key={i}
              inst={it}
              onChange={(next) => setItems(items.map((x, idx) => (idx === i ? next : x)))}
              onRemove={() => setItems(items.filter((_, idx) => idx !== i))}
            />
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
      </div>
    </div>
  );
}
